import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Shield, ShieldCheck, Warning, Copy, CheckCircle } from '@phosphor-icons/react';

/* 2FA / Two-Factor Authentication setup page.
 * • Anyone can opt-in. Admin/Owner are STRONGLY recommended (banner shows).
 * • Uses Google Authenticator / Authy / Microsoft Authenticator (TOTP).
 * • Backend: /api/auth/2fa/{status,setup,verify-and-enable,disable}
 */
export default function TwoFactorSetupPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const forcedByPolicy = searchParams.get('required') === '1';
  const [status, setStatus] = useState(null);
  const [setup, setSetup] = useState(null);   // {secret, provisioning_uri}
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmDisable, setConfirmDisable] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get('/auth/2fa/status'); setStatus(data); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Status load fail'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const beginSetup = async () => {
    setBusy(true);
    try { const { data } = await api.post('/auth/2fa/setup'); setSetup(data); setCode(''); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Setup fail'); }
    finally { setBusy(false); }
  };

  const enable = async () => {
    if (code.length < 6) return toast.error('6-digit code daalo');
    setBusy(true);
    try {
      await api.post('/auth/2fa/verify-and-enable', { code });
      toast.success('✅ 2FA enabled — agle login me Authenticator code maanga jayega');
      setSetup(null); setCode(''); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Enable fail'); }
    finally { setBusy(false); }
  };

  const disable = async () => {
    if (code.length < 6) return toast.error('6-digit code daalo');
    setBusy(true);
    try {
      await api.post('/auth/2fa/disable', { code });
      toast.success('2FA disable ho gaya');
      setConfirmDisable(false); setCode(''); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Disable fail'); }
    finally { setBusy(false); }
  };

  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success('Copied'); };

  if (!status) return <div className="p-6 text-sm text-gray-500">Loading…</div>;

  return (
    <div className="max-w-2xl mx-auto space-y-5" data-testid="twofa-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Shield size={24} weight="duotone" className="text-violet-700" />
          Two-Factor Authentication (2FA)
        </h1>
        <p className="text-sm text-gray-500 mt-1">Bank-grade security — password ke alawa Authenticator app ka 6-digit code bhi maanga jayega.</p>
      </div>

      {forcedByPolicy && !status.enabled && (
        <div className="bg-rose-50 border-2 border-rose-300 rounded-lg p-4" data-testid="twofa-forced-banner">
          <div className="flex items-start gap-3">
            <Warning size={24} weight="fill" className="text-rose-600 shrink-0 mt-0.5" />
            <div>
              <div className="font-bold text-rose-900">🔒 2FA setup mandatory hai</div>
              <p className="text-xs text-rose-800 mt-1">
                Aapke role ke liye Owner ne 2FA compulsory kar rakha hai. Neeche steps follow karke abhi enable karo — tabhi main app access hoga.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Status card */}
      <Card>
        <CardContent className="p-5">
          {status.enabled ? (
            <div className="flex items-start gap-3">
              <ShieldCheck size={32} weight="duotone" className="text-emerald-600 shrink-0" />
              <div className="flex-1">
                <div className="font-bold text-emerald-700">✓ 2FA enabled hai</div>
                <div className="text-xs text-gray-500 mt-0.5">Aapka account secure hai. Login ke time Authenticator app ka code dena hoga.</div>
                {!status.is_admin && (
                  <Button size="sm" variant="outline" onClick={() => setConfirmDisable(true)} className="mt-3 text-rose-600 border-rose-300 hover:bg-rose-50" data-testid="twofa-disable-btn">
                    Disable 2FA
                  </Button>
                )}
                {status.is_admin && (
                  <div className="mt-3 text-[11px] bg-amber-50 border border-amber-200 text-amber-900 rounded p-2">
                    🔒 Admin/Owner ka 2FA self-disable nahi ho sakta — security policy. Doosre owner se reset karwana padega.
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-3">
              <Warning size={32} weight="duotone" className="text-amber-500 shrink-0" />
              <div className="flex-1">
                <div className="font-bold text-amber-700">2FA disabled hai</div>
                {status.is_required ? (
                  <div className="text-xs text-rose-700 font-semibold mt-0.5">⚠️ Aap admin/owner role me ho — 2FA highly recommended hai. Abhi enable kar lo.</div>
                ) : (
                  <div className="text-xs text-gray-500 mt-0.5">Optional but strongly recommended for staff/distributor accounts.</div>
                )}
                {!setup && (
                  <Button size="sm" onClick={beginSetup} disabled={busy} className="mt-3 bg-violet-600 hover:bg-violet-700" data-testid="twofa-enable-btn">
                    {busy ? 'Generating…' : '🛡️ Enable 2FA'}
                  </Button>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Setup wizard */}
      {setup && !status.enabled && (
        <Card data-testid="twofa-setup-wizard">
          <CardContent className="p-5 space-y-4">
            <div className="font-bold text-gray-800">Step 1: Authenticator app me account add karein</div>
            <ul className="text-xs text-gray-600 list-disc pl-5 space-y-1">
              <li>Google Authenticator / Microsoft Authenticator / Authy / 1Password — koi bhi chalega</li>
              <li>App khol ke "+" pe click karein → "Scan QR" ya "Enter setup key"</li>
              <li>Niche diya QR scan karein (ya manual secret type karein)</li>
            </ul>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border rounded-lg p-3 bg-white text-center">
                <div className="text-[10px] font-bold text-gray-500 uppercase mb-2">QR Code</div>
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(setup.provisioning_uri)}`}
                  alt="2FA QR"
                  className="mx-auto border"
                  data-testid="twofa-qr"
                />
                <div className="text-[10px] text-gray-400 mt-2">Phone se scan karo</div>
              </div>
              <div className="border rounded-lg p-3 bg-gray-50">
                <div className="text-[10px] font-bold text-gray-500 uppercase">Manual entry — backup</div>
                <div className="text-xs text-gray-600 mt-1">Account: <b>{setup.account}</b></div>
                <div className="text-xs text-gray-600 mt-1">Issuer: <b>{setup.issuer}</b></div>
                <div className="mt-2">
                  <div className="text-[10px] text-gray-500 uppercase">Secret key</div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <code className="flex-1 text-xs bg-white border px-2 py-1.5 rounded font-mono break-all" data-testid="twofa-secret">{setup.secret}</code>
                    <button onClick={() => copy(setup.secret)} className="p-1.5 border rounded hover:bg-white" title="Copy"><Copy size={14} /></button>
                  </div>
                </div>
                <div className="mt-3 bg-amber-50 border border-amber-200 rounded p-2 text-[10px] text-amber-900">
                  💾 <b>Secret save kar lo</b> kahin safe jagah. Phone kho jaye to admin se reset karwana padega.
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <div className="font-bold text-gray-800 mb-2">Step 2: Authenticator app ka 6-digit code daalein</div>
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <Input
                    value={code}
                    onChange={e => setCode(e.target.value.replace(/[^\d]/g, '').slice(0, 6))}
                    placeholder="123456"
                    className="h-11 text-lg font-mono tracking-widest text-center"
                    maxLength={6}
                    data-testid="twofa-enable-code"
                    onKeyDown={e => e.key === 'Enter' && enable()}
                  />
                </div>
                <Button onClick={enable} disabled={busy || code.length < 6} className="h-11 bg-emerald-600 hover:bg-emerald-700" data-testid="twofa-confirm-enable">
                  <CheckCircle size={16} className="mr-1" /> Confirm & Enable
                </Button>
                <Button variant="outline" onClick={() => { setSetup(null); setCode(''); }} disabled={busy} className="h-11">Cancel</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Disable confirm */}
      {confirmDisable && (
        <Card>
          <CardContent className="p-5 space-y-3">
            <div className="font-bold text-rose-700">Disable 2FA — current code daalein</div>
            <p className="text-xs text-gray-500">Apne Authenticator app me jo code abhi dikh raha hai woh daal kar 2FA off kar sakte ho.</p>
            <div className="flex gap-2">
              <Input
                value={code}
                onChange={e => setCode(e.target.value.replace(/[^\d]/g, '').slice(0, 6))}
                placeholder="123456"
                className="h-10 text-base font-mono tracking-widest"
                maxLength={6}
                data-testid="twofa-disable-code"
              />
              <Button onClick={disable} disabled={busy || code.length < 6} variant="outline" className="text-rose-600 border-rose-300 hover:bg-rose-50" data-testid="twofa-confirm-disable">Disable</Button>
              <Button variant="outline" onClick={() => { setConfirmDisable(false); setCode(''); }}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded p-3 text-[11px] text-blue-900">
        <b>How it works:</b> 2FA enable hone ke baad har login pe password + 6-digit code dono maanga jayega. Code phone ki Authenticator app me har 30 sec me change hota hai. Iss feature se aapka account password chura bhi liya jaye to bhi safe rahega.
        <br /><br />
        <b>Recovery:</b> Phone kho jaye ya app uninstall ho jaye to admin/owner se contact karein — woh aapka 2FA reset kar sakta hai.
        <br /><br />
        Logged in as: <b>{user?.email}</b> · Role: <b>{user?.role}</b>
      </div>
    </div>
  );
}
